import { useAuth } from "AuthProvider";
import { Form as FormikForm, Formik } from "formik";
import useFetch from "hooks/useFetch";
import React, { useState } from "react";
import {
  Alert,
  Button,
  Card,
  ConfirmDialog,
  FormikTextarea,
  Icon,
  PageHeader,
  useToast,
} from "stories/Admin";
import * as Yup from "yup";
import SectionTabs from "pages/AdminLayout/SectionTabs";
import styles from "./GlobalPushPage.module.scss";

interface PushNotificationForm {
  body: string;
}

const MAX_CHAR_LIMIT = 255;

const validationSchema = Yup.object().shape({
  body: Yup.string()
    .required("Melding er påkrevd")
    .max(MAX_CHAR_LIMIT, `Melding kan ikke overstige ${MAX_CHAR_LIMIT} tegn`),
});

const GlobalPushPage: React.FC = () => {
  const { uid, selectedOrgNumber } = useAuth();
  const { isLoading, error, execute } = useFetch<any>();
  const toast = useToast();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formValues, setFormValues] = useState<PushNotificationForm | null>(
    null,
  );

  const handleSubmit = (values: PushNotificationForm) => {
    setFormValues(values);
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    if (!formValues) return;

    const currentDate = new Date().toISOString();

    const payload = {
      uid: uid,
      orgnr: selectedOrgNumber,
      title: "",
      imgurl: "",
      body: formValues.body,
      date: currentDate,
    };

    const response = await execute(
      "/api/v1/notification/send-notification-to-all",
      "POST",
      payload,
    );

    if (response) {
      toast.success("Push-melding sendt");
      setFormValues(null);
      setShowConfirmModal(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Push-meldinger"
        subtitle="Send varsler til alle brukere av iBønn, eller til alle moskéadminer."
      />
      <SectionTabs />

      <Formik
        initialValues={{
          body: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty, values, setFieldValue }) => (
          <FormikForm className={styles.layout} noValidate>
            <Card title="Ny melding til alle brukere">
              <FormikTextarea
                name="body"
                label="Melding"
                required
                rows={3}
                placeholder="Skriv meldingen …"
                maxLength={MAX_CHAR_LIMIT}
                maxChars={MAX_CHAR_LIMIT}
                onChange={(e) =>
                  setFieldValue("body", e.target.value.slice(0, MAX_CHAR_LIMIT))
                }
              />
              <Alert tone="warn">
                Meldingen går til alle brukere av appen. Du må bekrefte før den
                sendes.
              </Alert>
              {error && <Alert tone="error">Feilmelding: {error.text}</Alert>}
              <div className={styles.actions}>
                <Button
                  icon={<Icon name="bell" size={16} />}
                  onClick={() => handleSubmit(values)}
                  loading={isLoading}
                  disabled={!(isValid && dirty)}
                >
                  Send til alle
                </Button>
              </div>
            </Card>

            <section className={styles.preview} aria-label="Forhåndsvisning">
              <span className={styles.previewLabel}>FORHÅNDSVISNING</span>
              <div className={styles.notification}>
                <span className={styles.appIcon}>
                  <Icon name="moon" size={18} />
                </span>
                <span className={styles.notificationText}>
                  <b>iBønn</b>
                  <span>{values.body || "Meldingen vises her."}</span>
                </span>
              </div>
            </section>
          </FormikForm>
        )}
      </Formik>

      <ConfirmDialog
        open={showConfirmModal}
        title="Send til alle brukere?"
        confirmLabel="Bekreft og send"
        busy={isLoading}
        onConfirm={handleConfirmSubmit}
        onCancel={() => setShowConfirmModal(false)}
      >
        «{formValues?.body}» sendes nå til <b>alle brukere</b>. Dette kan ikke
        angres.
      </ConfirmDialog>
    </>
  );
};

export default GlobalPushPage;
